import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Package,
  X
} from 'lucide-react';
import { productsAPI, brandsAPI, equipmentTypesAPI, partCategoriesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import SearchableSelect from './SearchableSelect';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [availableFilters, setAvailableFilters] = useState({
    brands: [],
    equipmentTypes: [],
    partCategories: []
  });

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, filters]);

  useEffect(() => {
    fetchAvailableFilters();
  }, []);

  // Listen for filter selection event to auto-close filters
  useEffect(() => {
    const handleFilterSelected = () => {
      // Close filters panel after a short delay
      setTimeout(() => {
        setShowFilters(false);
      }, 300);
    };

    window.addEventListener('filterSelected', handleFilterSelected);
    return () => window.removeEventListener('filterSelected', handleFilterSelected);
  }, []);

  const fetchAvailableFilters = async () => {
    try {
      console.log('Fetching filter options from APIs...');
      
      const [brandsRes, equipmentTypesRes, partCategoriesRes] = await Promise.all([
        brandsAPI.getAllSimple(),
        equipmentTypesAPI.getAllSimple(),
        partCategoriesAPI.getAllSimple()
      ]);
      
      console.log('API Responses:', {
        brands: brandsRes.data,
        equipmentTypes: equipmentTypesRes.data,
        partCategories: partCategoriesRes.data
      });
      
      // Extract data from responses - matching ProductForm.jsx structure
      const brands = brandsRes.data?.data?.brands || [];
      const equipmentTypes = equipmentTypesRes.data?.data?.equipmentTypes || [];
      const partCategories = partCategoriesRes.data?.data?.partCategories || [];
      
      console.log('Extracted data:', {
        brands,
        equipmentTypes,
        partCategories
      });
      
      // Map to names for the select options
      setAvailableFilters({
        brands: brands.map(b => b.name),
        equipmentTypes: equipmentTypes.map(e => e.name),
        partCategories: partCategories.map(p => p.name)
      });
      
      console.log('Available Filters Set Successfully:', {
        brands: brands.map(b => b.name),
        equipmentTypes: equipmentTypes.map(e => e.name),
        partCategories: partCategories.map(p => p.name)
      });
    } catch (error) {
      console.error('Failed to fetch filters:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load filter options');
      
      // Set empty arrays as fallback
      setAvailableFilters({
        brands: [],
        equipmentTypes: [],
        partCategories: []
      });
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...filters
      };

      console.log('Fetching products with params:', params);
      
      const response = await productsAPI.getAllAdmin(params);
      console.log('Products response:', response.data);
      
      setProducts(response.data.data.products);
      setTotalPages(response.data.data.pagination.totalPages);
      setTotalItems(response.data.data.pagination.totalItems);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClick = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value === '' || value === null || value === undefined) {
        // Remove the filter if value is empty
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setSearchInput('');
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsAPI.delete(id);
        toast.success('Product deleted successfully');
        fetchProducts();
      } catch (error) {
        console.error('Failed to delete product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await productsAPI.toggleStatus(id);
      toast.success(`Product ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchProducts();
    } catch (error) {
      console.error('Failed to toggle product status:', error);
      toast.error('Failed to toggle product status');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary font-wix">Products</h1>
          <p className="text-secondary/70 font-wix">Manage your truck spare parts catalog</p>
        </div>
        <Link
          to="/admin/products/new"
          className="btn-primary inline-flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Product</span>
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary/60" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 font-wix"
              />
            </div>
            <button
              onClick={handleSearchClick}
              className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-2xl text-sm font-medium hover:bg-primary/10 transition-all duration-300 font-wix"
            >
              OK
            </button>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-6 py-3 border border-gray-200 rounded-2xl text-sm font-medium text-secondary bg-white hover:bg-primary/10 hover:text-primary transition-all duration-300 font-wix"
          >
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 p-6 bg-gray-50 rounded-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Brand Filter */}
              <SearchableSelect
                label="Brand"
                value={filters.brand || ''}
                onChange={(value) => handleFilterChange('brand', value)}
                options={availableFilters.brands || []}
                placeholder="Search for a Brand..."
                emptyLabel="All Brands"
              />

              {/* Equipment Type Filter */}
              <SearchableSelect
                label="Equipment Type"
                value={filters.typeOfEquipment || ''}
                onChange={(value) => handleFilterChange('typeOfEquipment', value)}
                options={availableFilters.equipmentTypes || []}
                placeholder="Search for an Equipment Type..."
                emptyLabel="All Types"
              />

              {/* Part Category Filter */}
              <SearchableSelect
                label="Part Category"
                value={filters.partCategory || ''}
                onChange={(value) => handleFilterChange('partCategory', value)}
                options={availableFilters.partCategories || []}
                placeholder="Search for a Part Category..."
                emptyLabel="All Categories"
              />

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-secondary mb-2 font-wix">
                  Status
                </label>
                <select
                  value={filters.isActive || ''}
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 font-wix"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

            </div>

            {/* Clear Filters */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-6 py-3 border border-gray-200 rounded-2xl text-sm font-medium text-secondary bg-white hover:bg-primary/10 hover:text-primary transition-all duration-300 font-wix"
              >
                <X className="h-5 w-5 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="card overflow-hidden">
        {/* Products Count Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-secondary font-wix">
            All Products
            {totalItems > 0 && (
              <span className="ml-2 text-primary">({totalItems})</span>
            )}
          </h2>
        </div>

        {products.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parts Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Part Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={product.image}
                            alt={product.partsName}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {product.partsName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {product.typeOfEquipment?.name || product.typeOfEquipment}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.brand?.name || product.brand}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.model}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.partsNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.partCategory?.name || product.partCategory}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center">
                        <button
                          onClick={() => handleToggleStatus(product._id, product.isActive)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                            product.isActive ? 'bg-primary' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ease-in-out ${
                              product.isActive ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`ml-2 text-xs font-medium ${
                          product.isActive ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {product.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/products/${product._id}/edit`}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(product._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || Object.keys(filters).length > 0
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating a new product.'}
            </p>
            <div className="mt-6">
              <Link
                to="/admin/products/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Link>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 font-wix">
                  Showing{' '}
                  <span className="font-medium">{totalItems > 0 ? (currentPage - 1) * 10 + 1 : 0}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * 10, totalItems)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{totalItems}</span>
                  {' '}products
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary border-primary text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
